import React, { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { 
  Building2, Users, Briefcase, UserCheck, Calendar, Award, 
  TrendingUp, AlertTriangle, CheckCircle2, ShieldCheck, ArrowUpRight, Flame, Layers,
  Search, Video, Clock, ChevronRight, Zap, Sparkles, FileText, Mail, MessageSquare, Activity, Archive, Stethoscope
} from "lucide-react";
import ExecutiveNotificationCenter from "./ExecutiveNotificationCenter";

export const ExecutiveQuickSearch = ({ applications = [], departmentSummaries = [] }) => {
  const [query, setQuery] = useState("");
  const navigate = useNavigate();

  const results = query.trim() ? applications.filter(app => {
    const q = query.toLowerCase();
    return (
      (app.full_name || "").toLowerCase().includes(q) ||
      (app.email || "").toLowerCase().includes(q) ||
      (app.phone || app.phone_number || "").toLowerCase().includes(q) ||
      (app.application_id || app.reference_number || "").toLowerCase().includes(q) ||
      (app.position_title || app.job_role || "").toLowerCase().includes(q) ||
      (app.department || "").toLowerCase().includes(q)
    );
  }).slice(0, 6) : [];

  const handleSelectResult = (r) => {
    setQuery("");
    navigate(`/admin/applications?candidateId=${r.id}`);
  };

  return (
    <div className="p-4 rounded-2xl border border-cyan-500/30 bg-slate-900/80 backdrop-blur-md mb-8 relative" data-testid="executive-quick-search">
      <div className="relative">
        <Search className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-cyan-400" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Global Executive Search: candidate name, position, department, reference ID, email, phone..."
          className="w-full pl-12 pr-4 py-3 rounded-xl border border-white/10 bg-black/50 text-sm text-white placeholder-white/40 focus:outline-none focus:border-cyan-400/60 font-mono"
        />
        {query && (
          <button
            onClick={() => setQuery("")}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-xs text-white/40 hover:text-white cursor-pointer"
          >
            Clear
          </button>
        )}
      </div>

      {/* Quick Search Results Dropdown */}
      {query.trim() && (
        <div className="mt-3 p-3 rounded-xl bg-slate-950 border border-cyan-500/30 space-y-2 max-h-80 overflow-y-auto">
          <div className="text-[10px] uppercase font-mono text-cyan-300/60 px-2">
            Matching Candidates & Entities ({results.length})
          </div>
          {results.length === 0 ? (
            <div className="p-3 text-xs text-white/40 text-center">No matching entities found.</div>
          ) : (
            results.map((r) => (
              <div
                key={r.id}
                onClick={() => handleSelectResult(r)}
                className="p-3 rounded-lg bg-white/5 hover:bg-cyan-400/10 border border-white/5 hover:border-cyan-400/30 flex items-center justify-between cursor-pointer transition-colors"
              >
                <div>
                  <div className="text-xs font-semibold text-white group-hover:text-cyan-300">{r.full_name}</div>
                  <div className="text-[11px] text-cyan-300/70 font-mono">{r.position_title || r.job_role} • {r.application_id || 'Ref ID'}</div>
                </div>
                <div className="text-right text-[10px] font-mono">
                  <span className="text-emerald-300 block">{r.status || 'Active'}</span>
                  <span className="text-white/40">{r.department || 'Operations'}</span>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export const TodaysKpiSnapshot = ({ todaySnapshot }) => {
  const navigate = useNavigate();

  const items = [
    { label: "Applications Today", val: todaySnapshot?.appsToday ?? 1, color: "text-cyan-300 border-cyan-500/30 hover:border-cyan-400 hover:bg-cyan-400/10", nav: "/admin/applications?filter=today" },
    { label: "Interviews Today", val: todaySnapshot?.interviewsToday ?? 1, color: "text-amber-300 border-amber-500/30 hover:border-amber-400 hover:bg-amber-400/10", nav: "/admin/interviews?filter=today" },
    { label: "Completed Today", val: todaySnapshot?.interviewsCompletedToday ?? 0, color: "text-emerald-300 border-emerald-500/30 hover:border-emerald-400 hover:bg-emerald-400/10", nav: "/admin/interviews?filter=completed" },
    { label: "Offers Released", val: todaySnapshot?.offersReleasedToday ?? 0, color: "text-purple-300 border-purple-500/30 hover:border-purple-400 hover:bg-purple-400/10", nav: "/admin/applications?filter=shortlisted" },
    { label: "Offers Accepted", val: todaySnapshot?.offersAcceptedToday ?? 0, color: "text-teal-300 border-teal-500/30 hover:border-teal-400 hover:bg-teal-400/10", nav: "/admin/applications?filter=selected" },
    { label: "Joined Today", val: todaySnapshot?.joinedToday ?? 0, color: "text-green-300 border-green-500/30 hover:border-green-400 hover:bg-green-400/10", nav: "/admin/applications?filter=selected" },
    { label: "Pending Reviews", val: todaySnapshot?.pendingReviews ?? 0, color: "text-pink-300 border-pink-500/30 hover:border-pink-400 hover:bg-pink-400/10", nav: "/admin/applications?filter=new" },
    { label: "Pending Interviews", val: todaySnapshot?.pendingInterviews ?? 0, color: "text-orange-300 border-orange-500/30 hover:border-orange-400 hover:bg-orange-400/10", nav: "/admin/interviews?filter=pending" },
  ];

  const handleMetricClick = (it) => {
    console.debug("[CLICK DETECTED: TODAY SNAPSHOT METRIC]", { cardName: it.label, targetRoute: it.nav, timestamp: new Date().toISOString() });
    console.debug("[HANDLER EXECUTED: TODAY SNAPSHOT]", { cardName: it.label });
    console.debug("[NAVIGATE CALLED]", { targetRoute: it.nav });
    navigate(it.nav);
    console.debug("[ROUTE UPDATED]", { targetRoute: it.nav, newWindowPath: window.location.pathname });
  };

  return (
    <div className="p-6 rounded-2xl border border-white/10 bg-slate-900/40 backdrop-blur-md mb-8" data-testid="todays-kpi-snapshot">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-light text-white flex items-center gap-2">
          <Clock className="w-5 h-5 text-cyan-400" /> Today's Operational Snapshot
        </h3>
        <span className="text-xs font-mono text-cyan-300/60 uppercase">Click metric to drill down</span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
        {items.map((it, idx) => (
          <button
            key={idx}
            onClick={() => handleMetricClick(it)}
            className={`p-3 rounded-xl border bg-black/40 text-center font-mono cursor-pointer transition-all ${it.color}`}
            title={`View ${it.label}`}
          >
            <div className="text-xl font-bold">{it.val}</div>
            <div className="text-[10px] uppercase tracking-wider opacity-75 mt-1 font-sans">{it.label}</div>
          </button>
        ))}
      </div>
    </div>
  );
};

export const UpcomingInterviewsWidget = ({ upcomingInterviews = [] }) => {
  const navigate = useNavigate();

  return (
    <div className="p-6 rounded-2xl border border-white/10 bg-slate-900/40 backdrop-blur-md mb-8" data-testid="upcoming-interviews-widget">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
        <div>
          <h3 className="text-lg font-light text-white flex items-center gap-2">
            <Calendar className="w-5 h-5 text-cyan-400" /> Upcoming Candidate Interviews (Next 7 Days)
          </h3>
          <p className="text-xs text-cyan-300/60 mt-0.5">Scheduled panel interviews, technical rounds, and executive evaluations</p>
        </div>
        <button
          onClick={() => navigate("/admin/interviews")}
          className="text-xs text-cyan-300 hover:text-cyan-200 font-mono flex items-center gap-1 underline"
        >
          View Complete Interview Schedule <ChevronRight className="w-3 h-3" />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {upcomingInterviews.map((int, i) => (
          <div key={i} className="p-4 rounded-xl border border-white/10 bg-black/40 hover:border-cyan-400/30 transition-all">
            <div className="flex items-start justify-between mb-2">
              <div>
                <span className="text-[10px] font-mono text-cyan-300 uppercase block">{int.department}</span>
                <div className="text-sm font-semibold text-white mt-0.5">{int.candidateName}</div>
                <div className="text-xs text-white/60">{int.position}</div>
              </div>
              <span className="px-2.5 py-0.5 rounded-full text-[10px] font-mono uppercase bg-cyan-400/10 text-cyan-300 border border-cyan-400/30">
                {int.status}
              </span>
            </div>

            <div className="space-y-1.5 text-xs text-white/80 font-mono p-3 rounded-lg bg-white/5 border border-white/5 my-3">
              <div className="flex items-center gap-2 text-cyan-300">
                <Calendar className="w-3.5 h-3.5 text-cyan-400" />
                <span>{int.date}</span>
              </div>
              <div className="flex items-center gap-2 text-purple-300">
                <Video className="w-3.5 h-3.5 text-purple-400" />
                <span>Mode: {int.mode}</span>
              </div>
              <div className="flex items-center gap-2 text-amber-300 text-[11px]">
                <Users className="w-3.5 h-3.5 text-amber-400" />
                <span>Panel: {int.interviewer}</span>
              </div>
            </div>

            <button
              onClick={() => navigate("/admin/applications")}
              className="w-full py-2 rounded-lg bg-cyan-400/10 hover:bg-cyan-400/20 text-cyan-300 border border-cyan-400/30 text-xs font-mono transition-colors cursor-pointer text-center"
            >
              Open Candidate Workspace
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export const TopCandidatesLeaderboard = ({ topCandidates = [] }) => {
  const navigate = useNavigate();

  return (
    <div className="p-6 rounded-2xl border border-white/10 bg-slate-900/40 backdrop-blur-md mb-8" data-testid="top-candidates-leaderboard">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
        <div>
          <h3 className="text-lg font-light text-white flex items-center gap-2">
            <Award className="w-5 h-5 text-amber-400" /> Top Candidates by CQI Leaderboard
          </h3>
          <p className="text-xs text-cyan-300/60 mt-0.5">Highest ranked quantitative & executive talent by Candidate Quality Index</p>
        </div>
        <button
          onClick={() => navigate("/admin/applications")}
          className="text-xs text-cyan-300 hover:text-cyan-200 font-mono flex items-center gap-1 underline cursor-pointer"
        >
          View All Applications <ChevronRight className="w-3 h-3" />
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left text-xs font-mono">
          <thead>
            <tr className="border-b border-white/10 text-white/40 uppercase tracking-wider">
              <th className="pb-3 pl-2">Rank</th>
              <th className="pb-3">Candidate Name</th>
              <th className="pb-3">Department</th>
              <th className="pb-3">Position</th>
              <th className="pb-3 text-center">CQI Score</th>
              <th className="pb-3 text-center">Priority</th>
              <th className="pb-3 text-center">Current CTC</th>
              <th className="pb-3 text-center">Expected CTC</th>
              <th className="pb-3 text-center">Growth %</th>
              <th className="pb-3 text-right pr-2">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5 text-white/80">
            {topCandidates.map((c, idx) => (
              <tr 
                key={c.id || idx} 
                onClick={() => navigate(`/admin/applications?candidateId=${c.id}`)}
                className="hover:bg-cyan-400/10 transition-colors cursor-pointer"
              >
                <td className="py-3 pl-2 font-bold text-amber-300">#{idx + 1}</td>
                <td className="py-3 font-sans font-medium text-white group-hover:text-cyan-300">{c.full_name}</td>
                <td className="py-3 text-cyan-300/80">{c.department || "Quantitative Strategies"}</td>
                <td className="py-3 text-white/70">{c.position_title || c.job_role}</td>
                <td className="py-3 text-center text-emerald-300 font-bold text-sm">{c.computedCqi} / 100</td>
                <td className="py-3 text-center">
                  <span className="px-2 py-0.5 rounded bg-amber-500/10 text-amber-300 border border-amber-500/30 text-[10px]">
                    {c.priorityLabel}
                  </span>
                </td>
                <td className="py-3 text-center text-white/60">{c.current_ctc || 'N/A'}</td>
                <td className="py-3 text-center text-white/90">{c.expected_ctc || 'N/A'}</td>
                <td className="py-3 text-center text-cyan-300 font-bold">{c.computedGrowth}</td>
                <td className="py-3 text-right pr-2">
                  <button
                    onClick={(e) => { e.stopPropagation(); navigate(`/admin/applications?candidateId=${c.id}`); }}
                    className="px-2.5 py-1 rounded bg-cyan-400/10 hover:bg-cyan-400/20 text-cyan-300 border border-cyan-400/30 text-[10px] transition-colors cursor-pointer"
                  >
                    View Workspace
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const SmartExecutiveInsightsPanel = ({ executiveInsights = [] }) => {
  return (
    <div className="p-6 rounded-2xl border border-white/10 bg-slate-900/40 backdrop-blur-md mb-8" data-testid="smart-executive-insights">
      <h3 className="text-lg font-light text-white mb-4 flex items-center gap-2">
        <Sparkles className="w-5 h-5 text-cyan-400" /> Smart Executive Operational Insights
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs font-mono">
        {executiveInsights.map((insight, idx) => (
          <div key={idx} className="p-3.5 rounded-xl bg-black/40 border border-white/5 flex items-start gap-2.5">
            <Zap className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
            <span className="text-white/80 leading-relaxed">{insight}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export const ExecutiveQuickActionsGrid = () => {
  const navigate = useNavigate();

  const actions = [
    { label: "Departments & Roles", icon: Layers, path: "/admin/applications", color: "text-cyan-300" },
    { label: "Candidate Workspace", icon: Users, path: "/admin/applications", color: "text-emerald-300" },
    { label: "Interview Manager", icon: Calendar, path: "/admin/interviews", color: "text-amber-300" },
    { label: "Email Center", icon: Mail, path: "/admin/emails", color: "text-purple-300" },
    { label: "Contact Messages", icon: MessageSquare, path: "/admin/contacts", color: "text-pink-300" },
    { label: "Reports Archive", icon: Archive, path: "/admin/reports-archive", color: "text-teal-300" },
    { label: "System Diagnostics", icon: Stethoscope, path: "/admin/diagnostics", color: "text-orange-300" },
    { label: "Activity Logs", icon: Activity, path: "/admin/activities", color: "text-indigo-300" },
  ];

  const handleActionClick = (act) => {
    console.debug("[CLICK DETECTED: EXECUTIVE QUICK ACTION]", { buttonLabel: act.label, targetRoute: act.path, timestamp: new Date().toISOString() });
    console.debug("[HANDLER EXECUTED: QUICK ACTION]", { label: act.label });
    console.debug("[NAVIGATE CALLED]", { targetRoute: act.path });
    navigate(act.path);
    console.debug("[ROUTE UPDATED]", { targetRoute: act.path, newWindowPath: window.location.pathname });
  };

  return (
    <div className="p-6 rounded-2xl border border-white/10 bg-slate-900/40 backdrop-blur-md mb-8" data-testid="executive-quick-actions">
      <h3 className="text-lg font-light text-white mb-4 flex items-center gap-2">
        <Zap className="w-5 h-5 text-cyan-400" /> Executive Quick Navigation & Actions
      </h3>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {actions.map((act, i) => {
          const Icon = act.icon;
          return (
            <button
              key={i}
              onClick={() => handleActionClick(act)}
              className="p-4 rounded-xl border border-white/10 bg-black/40 hover:bg-cyan-400/10 hover:border-cyan-400/30 transition-all flex flex-col items-center text-center group cursor-pointer"
            >
              <Icon className={`w-6 h-6 mb-2 ${act.color}`} />
              <span className="text-xs text-white font-medium group-hover:text-cyan-300 transition-colors">{act.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export const EnterpriseKpiCards = ({ ceoOverview }) => {
  const navigate = useNavigate();

  const kpis = [
    { label: "Total Applications", val: ceoOverview?.totalApplications ?? 0, icon: Users, color: "#00FFD1", nav: "/admin/applications" },
    { label: "Open Vacancies", val: ceoOverview?.totalVacanciesRemaining ?? 0, icon: Briefcase, color: "#4facfe", nav: "/admin/applications" },
    { label: "Position Fill %", val: ceoOverview?.overallFillProgress ?? "0%", icon: TrendingUp, color: "#43e97b", nav: "/admin/applications" },
    { label: "Recruitment Health", val: ceoOverview?.recruitmentHealth ?? "95%", icon: ShieldCheck, color: "#00FFD1", nav: "/admin/diagnostics" },
    { label: "Average CQI", val: ceoOverview?.avgGlobalCqi ?? "85.2", icon: Award, color: "#f5576c", nav: "/admin/applications?filter=cqi90" },
    { label: "Average CTC Growth", val: ceoOverview?.avgGlobalCtcGrowth ?? "+32.4%", icon: ArrowUpRight, color: "#fa709a", nav: "/admin/applications?filter=outliers" },
    { label: "Immediate Joiners", val: ceoOverview?.immediateJoinersCount ?? 0, icon: UserCheck, color: "#00FFD1", nav: "/admin/applications?filter=immediate" },
    { label: "Compensation Outliers", val: ceoOverview?.compensationOutliersCount ?? 0, icon: AlertTriangle, color: "#ffb703", nav: "/admin/applications?filter=outliers" },
  ];

  const handleKpiClick = (k) => {
    console.debug("[CLICK DETECTED: ENTERPRISE KPI CARD]", { cardName: k.label, targetRoute: k.nav, timestamp: new Date().toISOString() });
    console.debug("[HANDLER EXECUTED: KPI CARD]", { cardName: k.label });
    console.debug("[NAVIGATE CALLED]", { targetRoute: k.nav });
    navigate(k.nav);
    console.debug("[ROUTE UPDATED]", { targetRoute: k.nav, newWindowPath: window.location.pathname });
  };

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8" data-testid="enterprise-kpi-cards">
      {kpis.map((k, i) => {
        const Icon = k.icon;
        return (
          <motion.button
            key={i}
            onClick={() => handleKpiClick(k)}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.04 }}
            className="p-4 rounded-2xl border border-white/10 bg-slate-900/40 backdrop-blur-md relative overflow-hidden group hover:border-cyan-400/40 hover:bg-cyan-400/5 transition-all text-left cursor-pointer"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-cyan-400/10 border border-cyan-400/30">
                <Icon className="w-4 h-4 text-cyan-300" />
              </div>
              <span className="text-[10px] text-white/40 uppercase font-mono tracking-wider">KPI</span>
            </div>
            <div className="text-2xl font-light text-white font-mono group-hover:text-cyan-300 transition-colors">{k.val}</div>
            <div className="text-xs text-cyan-300/60 uppercase tracking-wider mt-1">{k.label}</div>
          </motion.button>
        );
      })}
    </div>
  );
};

export const DepartmentHiringHeatmap = ({ departmentSummaries = [] }) => {
  const navigate = useNavigate();

  return (
    <div className="p-6 rounded-2xl border border-white/10 bg-slate-900/40 backdrop-blur-md mb-8" data-testid="department-hiring-heatmap">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
        <div>
          <h3 className="text-lg font-light text-white flex items-center gap-2">
            <Flame className="w-5 h-5 text-cyan-400" /> Executive Department Hiring Heatmap
          </h3>
          <p className="text-xs text-cyan-300/60 mt-0.5">Comprehensive departmental talent pipeline quality & health matrix</p>
        </div>
        <div className="flex items-center gap-2 text-[11px]">
          <span className="px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">🟢 Excellent</span>
          <span className="px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-300 border border-emerald-500/30">🟢 Strong</span>
          <span className="px-2 py-0.5 rounded bg-amber-500/10 text-amber-300 border border-amber-500/30">🟡 Moderate</span>
          <span className="px-2 py-0.5 rounded bg-orange-500/10 text-orange-300 border border-orange-500/30">🟠 Needs Attention</span>
          <span className="px-2 py-0.5 rounded bg-red-500/10 text-red-300 border border-red-500/30">🔴 Critical</span>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left text-xs font-mono">
          <thead>
            <tr className="border-b border-white/10 text-white/40 uppercase tracking-wider">
              <th className="pb-3 pl-2">Department</th>
              <th className="pb-3 text-center">Applications</th>
              <th className="pb-3 text-center">Avg CQI</th>
              <th className="pb-3 text-center">Avg Current CTC</th>
              <th className="pb-3 text-center">Avg Expected CTC</th>
              <th className="pb-3 text-center">CTC Growth</th>
              <th className="pb-3 text-center">Hiring Index</th>
              <th className="pb-3 text-right pr-2">Recruitment Health</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5 text-white/80">
            {departmentSummaries.map((dept, idx) => (
              <tr 
                key={idx} 
                onClick={() => navigate(`/admin/applications?search=${encodeURIComponent(dept.department)}`)}
                className="hover:bg-cyan-400/10 transition-colors cursor-pointer"
              >
                <td className="py-3.5 pl-2 font-sans font-medium text-white max-w-xs truncate group-hover:text-cyan-300">{dept.department}</td>
                <td className="py-3.5 text-center text-cyan-300 font-bold">{dept.totalApps}</td>
                <td className="py-3.5 text-center text-emerald-300">{dept.avgCqi}</td>
                <td className="py-3.5 text-center text-white/70">{dept.avgCurrentCtc}</td>
                <td className="py-3.5 text-center text-white/90">{dept.avgExpectedCtc}</td>
                <td className="py-3.5 text-center text-cyan-400 font-bold">{dept.avgCtcGrowth}</td>
                <td className="py-3.5 text-center text-purple-300">{dept.hiringIndex}</td>
                <td className="py-3.5 text-right pr-2">
                  <span className={`px-2.5 py-1 rounded-full text-[11px] font-sans font-medium border ${dept.healthBadge}`}>
                    {dept.healthStatus}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const SmartAlertsPanel = ({ smartAlerts = [] }) => {
  const navigate = useNavigate();

  const handleAlertClick = (alert) => {
    if (alert.title.includes("Compensation") || alert.message.includes("CTC")) {
      navigate("/admin/applications?filter=outliers");
    } else if (alert.title.includes("Immediate") || alert.message.includes("Immediate")) {
      navigate("/admin/applications?filter=immediate");
    } else if (alert.title.includes("Pending") || alert.message.includes("Review")) {
      navigate("/admin/applications?filter=new");
    } else {
      navigate("/admin/diagnostics");
    }
  };

  return (
    <div className="p-6 rounded-2xl border border-white/10 bg-slate-900/40 backdrop-blur-md mb-8" data-testid="smart-alerts-panel">
      <h3 className="text-lg font-light text-white mb-4 flex items-center gap-2">
        <AlertTriangle className="w-5 h-5 text-amber-400" /> Operational Smart Alerts
      </h3>
      <div className="space-y-3">
        {smartAlerts.map((alert, i) => (
          <div
            key={i}
            onClick={() => handleAlertClick(alert)}
            className={`p-4 rounded-xl border flex items-start gap-3 text-xs cursor-pointer hover:scale-[1.01] transition-transform ${
              alert.type === 'warning' ? 'bg-amber-500/10 border-amber-500/30 text-amber-200 hover:border-amber-400' :
              alert.type === 'info' ? 'bg-cyan-500/10 border-cyan-500/30 text-cyan-200 hover:border-cyan-400' :
              alert.type === 'action' ? 'bg-purple-500/10 border-purple-500/30 text-purple-200 hover:border-purple-400' :
              'bg-emerald-500/10 border-emerald-500/30 text-emerald-200 hover:border-emerald-400'
            }`}
          >
            {alert.type === 'warning' ? <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" /> :
             alert.type === 'info' ? <Users className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" /> :
             alert.type === 'action' ? <Layers className="w-4 h-4 text-purple-400 flex-shrink-0 mt-0.5" /> :
             <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />}
            <div className="flex-1">
              <div className="font-semibold text-white mb-0.5 flex items-center justify-between">
                <span>{alert.title}</span>
                <span className="text-[10px] text-cyan-300 underline font-mono">View Queue →</span>
              </div>
              <div className="opacity-90 leading-relaxed">{alert.message}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const CeoExecutiveOverview = ({ 
  ceoOverview, 
  departmentSummaries, 
  smartAlerts, 
  topCandidates, 
  upcomingInterviews, 
  todayKpiSnapshot, 
  executiveInsights,
  applications 
}) => {
  return (
    <div className="space-y-6" data-testid="ceo-executive-overview">
      {/* Global Quick Search */}
      <ExecutiveQuickSearch applications={applications} departmentSummaries={departmentSummaries} />

      {/* Header Banner */}
      <div className="p-6 rounded-2xl border border-cyan-500/30 bg-gradient-to-r from-slate-900/80 to-cyan-950/40 backdrop-blur-md relative overflow-hidden">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <div className="text-xs uppercase tracking-wider text-cyan-300 font-mono font-semibold flex items-center gap-1.5">
              <ShieldCheck className="w-4 h-4 text-cyan-400" /> Executive Command Center
            </div>
            <h2 className="text-2xl font-light text-white mt-1">CEO & Leadership Enterprise Overview</h2>
          </div>
          <div className="flex items-center gap-3">
            <span className="px-3.5 py-2 rounded-xl bg-cyan-400/10 text-cyan-300 border border-cyan-400/40 text-xs font-mono font-semibold">
              Health Index: {ceoOverview?.recruitmentHealth ?? "94.5%"}
            </span>
            <ExecutiveNotificationCenter applications={applications} />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs font-mono p-4 rounded-xl bg-black/40 border border-white/5">
          <div>
            <span className="text-white/40 block">Top Hiring Department:</span>
            <span className="text-emerald-300 font-semibold text-sm font-sans">{ceoOverview?.topDepartment}</span>
          </div>
          <div>
            <span className="text-white/40 block">Total Approved Headcount:</span>
            <span className="text-white font-semibold text-sm">{ceoOverview?.totalApprovedHeadcount} Positions</span>
          </div>
          <div>
            <span className="text-white/40 block">Overall Position Fill Progress:</span>
            <span className="text-cyan-300 font-semibold text-sm">{ceoOverview?.overallFillProgress}</span>
          </div>
        </div>
      </div>

      {/* Section 1: Today's Operational KPI Snapshot */}
      <TodaysKpiSnapshot todaySnapshot={todayKpiSnapshot} />

      {/* Enterprise KPI Cards */}
      <EnterpriseKpiCards ceoOverview={ceoOverview} />

      {/* Section 3: Department Hiring Heatmap */}
      <DepartmentHiringHeatmap departmentSummaries={departmentSummaries} />

      {/* Section 5: Upcoming Interviews (Next 7 Days) */}
      <UpcomingInterviewsWidget upcomingInterviews={upcomingInterviews} />

      {/* Section 6: Top 10 Candidates by CQI Leaderboard */}
      <TopCandidatesLeaderboard topCandidates={topCandidates} />

      {/* Smart Executive Operational Insights */}
      <SmartExecutiveInsightsPanel executiveInsights={executiveInsights} />

      {/* Operational Smart Alerts */}
      <SmartAlertsPanel smartAlerts={smartAlerts} />

      {/* Executive Quick Actions */}
      <ExecutiveQuickActionsGrid />
    </div>
  );
};

export default CeoExecutiveOverview;

